<template>
    <div class="is-full-mobile is-narrow is-relative is-app-sidebar tw:h-full">
        <aside id="application_sidebar" class="menu has-text-weight-semibold" :class="{ 'is-expanded': !summarize }">
            <ul v-if="summarize">
                <app-sidebar-summary-item
                    v-for="item in filteredItems"
                    :key="item.key"
                    :class="{ 'home-icon-item': item.data?.isHomeItem }"
                    :type="item.data?.type === 'folder' ? 'is-primary' : 'is-primary-light'"
                    :label="item.label"
                    :icon="item.icon || 'link'"
                    :to="item.data?.route || null"
                    @open="toggleSummary(item.data, item.key)"
                />
            </ul>

            <!--            <div v-else class="course-menu-container">-->
            <!--                <ul>-->
            <!--                    <li v-for="(item, index) in filteredItems" :key="index">-->
            <!--                        <component-->
            <!--                            :is="`sidebar-${item.type}`"-->
            <!--                            :item="item"-->
            <!--                            :has-icon="hasIcon"-->
            <!--                            :display-items-always="item.displayItemsAlways"-->
            <!--                            :display-items-on-create="itemToOpen === index"-->
            <!--                        />-->
            <!--                    </li>-->
            <!--                </ul>-->
            <!--            </div>-->

            <Tree
                v-if="!summarize"
                v-model:expanded-keys="expandedKeys"
                class="tree tw:w-full"
                selection-mode="single"
                :value="filteredItems"
                :pt="{
                    nodeContent: {
                        class: 'tw:hover:bg-[var(--surface-hover)] tw:cursor-pointer'
                    },
                    nodeChildren: {
                        style: 'padding-inline-start: 1rem !important;'
                    },
                    nodeToggleButton: {
                        class: 'tw:order-last tw:ml-auto!'
                    }
                }"
                @node-select="onNodeSelect"
            >
                <template #default="{ node }">
                    <router-link
                        v-if="node.data?.route"
                        :to="node.data.route"
                        class="tree-node-link"
                    >
                        {{ node.label }}
                    </router-link>
                    <span v-else>{{ node.label }}</span>
                </template>
            </Tree>
        </aside>

        <div class="is-app-sidebar-meta tw:flex tw:flex-col">
            <brand-icon v-if="institutionIsStanbridge" class="tw:mx-auto" />

            <p>{{ appVersion }}</p>
        </div>
    </div>
</template>

<script>
import filter from 'lodash/filter.js';
import some from 'lodash/some.js';
import AppSidebarSummaryItem from '@/components/app/sidebar/AppSidebarSummaryItem.vue';
import BrandIcon from '@/components/elements/BrandIcon.vue';
import { initialState } from '@/Helpers.js';
import { useLayout } from '@/layouts/composables/layout.js';
import UserGroupMixin from '@/mixins/UserGroup.mixin.js';

/**
 * Convert items to PrimeVue Tree node format
 * @param {Object|Array} items - The items to convert
 * @param {String} parentKey - The parent key for generating unique keys
 * @returns {Array} - Array of tree nodes
 */
function convertItemsToNodes(items, parentKey = '') {
    if (!items) return [];

    // Convert to array if items is an object
    const itemsArray = Array.isArray(items) ? items : Object.values(items);

    return itemsArray.map((item, index) => {
        const nodeKey = parentKey ? `${parentKey}-${index}` : `${index}`;
        const node = {
            key: nodeKey,
            label: item.title,
            // eslint-disable-next-line no-nested-ternary
            icon: item.icon ? (item.icon.startsWith('pi ') ? item.icon : `pi pi-${item.icon}`) : undefined,
            data: {
                type: item.type,
                route: item.route || null,
                disabled: item.disabled || false,
                order: item.order,
                ...item,
            },
            leaf: item.type === 'item' || !item.items,
        };

        // Recursively convert child items
        if (item.items && Object.keys(item.items).length > 0) {
            node.children = convertItemsToNodes(item.items, nodeKey);
        }

        return node;
    });
}

export default {
    name: 'SidebarMenu',

    components: {
        AppSidebarSummaryItem,
        BrandIcon,
    },

    mixins: [UserGroupMixin],

    emits: ['update:expanded'],

    props: {
        items: {
            type: [Object, Array],
            required: false,
            default: () => {},
        },

        activePageFolder: {
            type: [Object, Array],
            required: false,
            default: () => {},
        },

        hasIcon: {
            type: Boolean,
            required: false,
            default: false,
        },

        forceExpanded: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    setup() {
        const { layoutState, toggleSidebarSummary } = useLayout();

        return {
            layoutState,
            toggleSidebarSummary,
        };
    },

    data: () => ({
        itemToOpen: undefined,
        appVersion: window.appVersion,
        expandedKeys: {},
    }),

    computed: {
        summarize() {
            if (this.forceExpanded) {
                return false;
            }
            return this.layoutState.sidebarSummaryMode;
        },

        authUser() {
            return this.$store.state.User;
        },

        filteredItems() {
            const homeItem = {
                key: 'home',
                label: 'Home',
                icon: 'pi pi-home',
                data: {
                    type: 'item',
                    route: { name: 'home' },
                    isHomeItem: true,
                },
                selectable: true,
                leaf: true,
            };

            let items = [];

            if (this.$route.name.includes('page')) {
                items = convertItemsToNodes(this.items);
            } else {
                const filteredItemsBeforeConversion = filter(this.items, item => {
                    if (!item.items) {
                        return true;
                    }

                    return some(item.items, child => {
                        if (child.canSee === undefined) {
                            return true;
                        }
                        return child.canSee;
                    });
                });

                items = convertItemsToNodes(filteredItemsBeforeConversion);
            }

            return [homeItem, ...items];
        },

        institutionIsStanbridge() {
            return initialState('InstitutionSettings.name') === 'Stanbridge University';
        },
    },

    watch: {
        summarize(newValue) {
            // Emit update when summary state changes (including from topbar button)
            this.$emit('update:expanded', !newValue);
        },
    },

    mounted() {
        // Emit initial state
        this.$emit('update:expanded', !this.summarize);

        if (this.itemToOpen) {
            this.expandedKeys = {
                [this.itemToOpen]: true,
            };
        }
    },

    methods: {
        toggleSummary(item, key) {
            if (item && item.type === 'folder') {
                this.itemToOpen = key;
            } else {
                this.itemToOpen = undefined;
            }

            this.expandedKeys = {
                [this.itemToOpen]: true,
            };

            this.toggleSidebarSummary(item);
            this.$emit('update:expanded', !this.summarize);
        },

        resetToSummary() {
            // Reset to summary view (collapsed state)
            if (!this.summarize) {
                this.toggleSidebarSummary();
            }
            this.itemToOpen = undefined;
            this.$emit('update:expanded', false);
        },

        /**
         * @param item
         * @returns {*|string}
         */
        getSummaryItemIcon(item) {
            if (item.icon) {
                return item.icon;
            }

            return item.type === 'folder' ? 'folder' : 'link';
        },

        onNodeSelect(node) {
            // Handle navigation via route - just toggle sidebar, router-link handles navigation
            if (node.data && node.data.route) {
                if (!this.forceExpanded) {
                    this.resetToSummary();
                }
                return;
            }

            // Toggle expansion for nodes with children
            if (node.children && node.children.length > 0) {
                if (this.expandedKeys[node.key]) {
                    const newKeys = { ...this.expandedKeys };
                    delete newKeys[node.key];
                    this.expandedKeys = newKeys;
                } else {
                    this.expandedKeys = {
                        ...this.expandedKeys,
                        [node.key]: true,
                    };
                }
            }
        },
    },
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

.is-app-sidebar {
    display: flex !important;
    flex-direction: column;
    background-color: transparent !important;

    #application_sidebar {
        background-color: transparent !important;
    }

    &-meta {
        margin-top: auto;
        padding-top: $size-5;
        text-align: center;

        .brandIcon {
            margin-bottom: $size-10;
            vertical-align: bottom;
        }

        p {
            font-size: $very-small-font-size;
            line-height: 1;
        }
    }
}

.nested-list.menu-list {
    li.hidden {
        border-top: none;
    }
}

ul.is-unstyled {
    list-style: none;
    margin: 0;
    padding: 0;
}

.course-menu-container {
    width: 100%;
    padding: 0;
    margin: 0;
}

.menu {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.is-expanded {
    align-items: start !important;
    padding-left: 0.95rem;
}

.home-icon-item {
    .e-icon,
    svg,
    i {
        font-size: 1.5rem !important;
        width: 1.5rem !important;
        height: 1.5rem !important;
    }

    .sidebar-badge-container {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
    }
}

.tree :deep(.p-tree-node-content) {
    padding: 0;
    padding-block: 0.25rem;
}

.tree-node-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-decoration: none;
    color: inherit;
    width: 100%;

    &:hover {
        text-decoration: none;
    }

    &.router-link-active {
        font-weight: bold;
    }
}
</style>
